<template>
  <div class="tg-map" v-bind="attrs.binds">
    <div
      ref="map"
      :class="{
        'tg-map__map': true,
        [`tg-map__map--hide-logo-${type}`]: hideLogo
      }"
    ></div>
    <div v-if="false" style="position:absolute;right:0px;top:0px;">
      props:{{ propsJson }}
    </div>
    <template v-if="map">
      <slot></slot>
    </template>
    <slot name="overlay"></slot>
  </div>
</template>
<script lang="ts">
import { typed } from 'tg-commons'
import { bindEvents, BuildInMapTypeId, GestureHandlingOptions, InfoWindowModeValues, LatLng, loadCachedMap, MapType, Objects, TgMapFactory, TgMapType, unwrapStringEnumValue, type AbstractMap, type AbstractMapEventMap, type BaseMap, type MapOptions, type MapStyle, type StringEnumValue, type WrapStringEnumValue } from 'tg-map-core'
import { computed, defineComponent, markRaw, watch, type PropType } from 'vue'
import { eventProp, optionalProp, requiredProp, stringEnumProp, stringUnionPropFromValues, useSplittedAttrs, type EventEmits, type EventProps, type Props } from '../utils/vue-utils'
import { MIXIN_MAP_NAME } from './map-mixin'

type TgMapEmits = {
  load: AbstractMap,
  error: any,
  'update:center': LatLng,
  'update:current-center': LatLng,
  'update:zoom': number,
  'update:map-type': MapType,
  'update:map-type-id': string,
  'update:last-center': LatLng,
}

export const tgMapProps = {
  /** type没做响应式, 但外部可以把type作为tg-map的key, 让type修改时完全重建tg-map */
  type: stringEnumProp(TgMapType, TgMapType.google),
  /**
   * 当对该属性使用双向绑定时, 改变center将触发update:center又反过来触发center改变, 最终导致无限循环...
   * 当前通过setCenter()时判断center是否有改变来避免该问题, 但为了避免可能存在的问题, 另外设计了如下机制:
   * - :center-sync-delay="300": 延时同步center, 防止center的值更新过快
   * - :current-center.sync="currentCenter": 实时获取center的值
   * - :last-center.sync="center": 获取tg-map销毁时的最后的center的值, 使用这种方式可以做到type切换时保留中心点位置
   * @see AbstractMapEventMap.center-changed
   */
  center: requiredProp(LatLng),
  /**
   * 同步center的延时
   * @default 300
   * @see center
   */
  centerSyncDelay: optionalProp(Number, 300),
  /**
   * 仅用来获取center的值, 需要设置center的值请使用`center`属性
   * @see center
   * @deprecated 用的太少, 故maptalks未实现该属性, 要实时获取中心点, 请将{@link centerSyncDelay}设为0
   */
  currentCenter: optionalProp(LatLng),
  /**
   * 仅用于获取tg-map销毁时的最后的center的值
   * @see center
   * @deprecated 用的太少, 故maptalks未实现该属性
   */
  lastCenter: optionalProp(LatLng),
  zoom: requiredProp(Number),
  /**
   * 该属性不会响应式更新, 要让它立即生效, 请参考{@link file://./../views/map/InfoDemo.vue#L3}
   * @see MapOptions.infoWindowMode
   */
  infoWindowMode: stringUnionPropFromValues(InfoWindowModeValues),
  gestureHandling: stringEnumProp(GestureHandlingOptions),
  fractionalZoom: optionalProp(Boolean),
  minZoom: optionalProp(Number),
  maxZoom: optionalProp(Number),
  mapStyle: optionalProp(Object as PropType<MapStyle>),
  mapTypeId: stringEnumProp(BuildInMapTypeId),
  /** 地图类型对象, 优先级比mapTypeId高 */
  mapType: optionalProp(Object),
  hideLogo: optionalProp(Boolean),
  onLoad: eventProp(),
  'onUpdate:center': eventProp(),
  'onUpdate:current-center': eventProp(),
  'onUpdate:last-center': eventProp(),
  'onUpdate:map-type': eventProp(),
  'onUpdate:map-type-id': eventProp(),
  'onUpdate:zoom': eventProp(),
  onError: eventProp(),
} satisfies Props<WrapStringEnumValue<Omit<MapOptions, 'buildInMapTypeId'>, {
  gestureHandling: typeof GestureHandlingOptions,
}> & {
  type: StringEnumValue<typeof TgMapType>,
  currentCenter?: LatLng,
  lastCenter?: LatLng,
  centerSyncDelay: number,
  mapTypeId?: StringEnumValue<typeof BuildInMapTypeId>,
  mapType?: MapType
  hideLogo?: boolean
}> & EventProps<TgMapEmits>

export type TgMapEventEmits = EventEmits<AbstractMapEventMap & TgMapEmits>

export default defineComponent({
  name: 'tg-map',
  provide() {
    return {
      [MIXIN_MAP_NAME]: computed(() => this.map),
    }
  },
  inheritAttrs: false,
  props: tgMapProps,
  /** 声明事件的类型信息, 详见: {@link splitAttrs} */
  emits: undefined as any as TgMapEventEmits,
  setup(props, { attrs }) {
    return {
      attrs: useSplittedAttrs(attrs, props),
      centerSyncTimeoutId: undefined as TimeoutId | undefined,
    }
  },
  data() {
    return {
      map: typed<BaseMap>(),
      isDestroyed: false,
    }
  },
  computed: {
    propsJson() {
      return Objects.toJsonSafely(this.$props)
    },
  },
  watch: {
    gestureHandling(value?: StringEnumValue<typeof GestureHandlingOptions>) {
      if (value) {
        this.map?.setGestureHandling(unwrapStringEnumValue(value, GestureHandlingOptions))
      }
    },
    minZoom(value) {
      this.map?.setMinZoom(value)
    },
    maxZoom(value) {
      this.map?.setMaxZoom(value)
    },
    mapStyle(value?: MapStyle) {
      this.map?.setMapStyle(value)
    },
    mapTypeId(value?: StringEnumValue<typeof BuildInMapTypeId>) {
      if (value) {
        this.map?.setBuildInMapTypeId(unwrapStringEnumValue(value, BuildInMapTypeId))
      }
    },
    mapType(value?: MapType) {
      this.map?.setMapType(value ?? MapType.NORMAL)
    },
    hideLogo(value?: boolean) {
      this.map?.setHideLogo(value ?? false)
    },
    fractionalZoom(value?: boolean) {
      this.map?.setFractionalZoom(value ?? false)
    },
  },
  async mounted() {
    // type的值需要局部化, 防止外部修改
    const type = unwrapStringEnumValue(this.type, TgMapType)
    try {
      await loadCachedMap(type)
    } catch (e) {
      this.$emit('error', e)
      return
    }
    if (this.isDestroyed) {
      console.warn(`tg-map(type=${type})已销毁, 不需要继续执行`)
      return
    }
    // options不要直接传this, 因为createMap方法内部会修改options...
    const map = TgMapFactory.createMap(type, this.$refs.map as HTMLElement, /* options: */{
      center: this.center,
      zoom: this.zoom,
      infoWindowMode: this.infoWindowMode,
      gestureHandling: unwrapStringEnumValue(this.gestureHandling, GestureHandlingOptions),
      minZoom: this.minZoom,
      maxZoom: this.maxZoom,
      mapStyle: this.mapStyle,
      buildInMapTypeId: unwrapStringEnumValue(this.mapTypeId, BuildInMapTypeId),
      hideLogo: this.hideLogo,
      fractionalZoom: this.fractionalZoom,
    })
    // map是复杂对象, 不需要转换成响应式对象
    this.map = markRaw(map)
    if (this.mapType) {
      map.setMapType(this.mapType)
    }

    // 未声明ready事件, 所以需要从attrs.listeners中查找是否存在
    if ('ready' in this.attrs.listeners) {
      console.error('地图载入完成的事件已经从ready改为了load, 请手动修改')
    }
    bindEvents(this.attrs.listeners, map, this.$emit, /* excludes: */['load'])
    // 需要先发送load(载入完成)事件, 保证外部能够获取到map对象
    this.$emit('load', map)

    // Baidu地图的setZoom/Center是区分顺序的, 若同时修改它们, 必须先setZoom再setCenter, 否则中心点会出现偏移
    // Google地图没这个问题
    // 为了规避该问题, 同时监听它们两个, 并按顺序执行set方法
    watch([
      () => this.zoom,
      () => this.center, //
    ], ([zoom, center], [oldZoom, oldCenter]) => {
      if (zoom !== oldZoom) {
        import.meta.env.DEV && console.debug('<tg-map> setZoom', center)
        map.setZoom(zoom)
      }
      if (center !== oldCenter) {
        const currentCenter = map.getCenter()
        if (!currentCenter.equals(center)) {
          import.meta.env.DEV && console.debug('<tg-map> setCenter', center)
          map.setCenter(center)
        }
      }
    })

    // 处理v-model(双向绑定)
    if (this.attrs.listenerProps['update:center']) {
      const updateCenter = () => this.$emit('update:center', map.getCenter())
      map.addEventListener('center-changed', () => {
        if (this.centerSyncDelay > 0) {
          clearTimeout(this.centerSyncTimeoutId)
          this.centerSyncTimeoutId = setTimeout(updateCenter, this.centerSyncDelay)
        } else {
          updateCenter()
        }
      })
    }
    if (this.attrs.listenerProps['update:current-center']) {
      // 初始值也得emit出去
      this.$emit('update:current-center', this.center)
      map.addEventListener('center-changed', () => {
        this.$emit('update:current-center', map.getCenter())
      })
    }
    if (this.attrs.listenerProps['update:zoom']) {
      map.addEventListener('zoom-changed', () => {
        this.$emit('update:zoom', map.getZoom())
      })
    }
    const isUpdateMapType = this.attrs.listenerProps['update:map-type']
    const isUpdateMapTypeId = this.attrs.listenerProps['update:map-type-id']
    if (isUpdateMapType || isUpdateMapTypeId) {
      map.addEventListener('map-type-changed', () => {
        const mapType = map.getMapType()
        isUpdateMapType && this.$emit('update:map-type', mapType)
        isUpdateMapTypeId && this.$emit('update:map-type-id', mapType.id)
      })
    }
  },
  unmounted() {
    this.isDestroyed = true
    if (this.map) {
      if (this.attrs.listenerProps['update:last-center']) {
        this.$emit('update:last-center', this.map.getCenter())
      }
      if (this.attrs.listenerProps['update:center']) {
        clearTimeout(this.centerSyncTimeoutId)
        // 最终的值一定要更新出去, 保证切换地图时center的位置能够保持
        this.$emit('update:center', this.map.getCenter())
      }
    }
  },
  methods: {
  },
})
</script>

<style lang="scss">
.tg-map {
  height: 100%;
  position: relative;

  &__map {
    width: 100%;
    height: 100%;

    &--hide-logo-google {
      .gm-style {
        // 隐藏logo
        a[target="_blank"]:has(>div>img) {
          display: none !important;
        }

        // 隐藏右下角的Keyboard shortcuts/Terms of Use等
        &>div:last-child>div:last-child>div {
          display: none;
        }
      }
    }

    &--hide-logo-baidu {
      // 隐藏logo
      div.anchorBL>a[target="_blank"]>img {
        display: none;
      }

      // 隐藏左下角的版权信息
      div.BMap_cpyCtrl {
        display: none;
      }
    }
  }
}
</style>
